import "./index.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import { plusCircleLine } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useGridFilterTabNavigation } from "~/shared/hooks/use-grid-filter-tab-navigation.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";
import { getDefaultAddressBookGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { useGetInternalUserPreferences } from "../profile/hooks/internal/use-get-internal-user-preferences.ts";
import { useInternalGridPreferencesSync } from "../profile/hooks/internal/use-internal-grid-preferences-sync.ts";
import { useGetUserPreferences } from "../profile/hooks/use-get-user-preferences.ts";
import { useGridPreferencesSync } from "../profile/hooks/use-grid-preferences-sync.ts";
import { AddAddressModal } from "./components/add-address-modal/add-address-modal.tsx";
import type { AddressBookAddressRef } from "./components/address-book-address/address-book-address.tsx";
import { AddressBookAddress } from "./components/address-book-address/address-book-address.tsx";
import { AddressBookHeader } from "./components/address-book-header/address-book-header.tsx";
import { MobileAddressBook } from "./components/mobile-address-book/mobile-address-book.tsx";
import { useAddressBook } from "./hooks/use-address-book.ts";
import { useAddressBookColumns } from "./hooks/use-address-book-columns.tsx";
import { useAddressBookFilteredData } from "./hooks/use-address-book-filtered-data.ts";
import { useAddressBookGridColumns } from "./hooks/use-address-book-grid-columns.ts";
import { useAddressBookNavigation } from "./hooks/use-address-book-navigation.ts";
import type { AddressBookAddressSubmitData } from "./types/address-book-types.ts";

export const AddressBook = () => {
	const { isInternal } = useDeploymentType();
	const isMobile = useIsMobile();
	const {
		activeCustomer: activeCustomerFromNav,
		selectedAddressBookItem,
		isCustomerLoading,
		isCustomerError,
		handleSelectCustomer,
		handleSelectAddressBookItem,
	} = useAddressBookNavigation();

	const {
		filter,
		handleFilterChange,
		sort,
		handleSortChange,
		skip,
		take,
		handlePageChange,
		isLoadingPreference,
	} = useGridState(GridIdentifiers["Address Book"], {
		initialSort: [{ field: "createdDate", dir: "desc" }],
		enableFilter: true,
	});

	useGridFilterTabNavigation();

	const [pendingSubmitData, setPendingSubmitData] =
		useState<AddressBookAddressSubmitData | null>(null);

	const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
	const [showAddAddressModal, setShowAddAddressModal] = useState(false);
	const addressBookAddressRef = useRef<AddressBookAddressRef>(null);

	const activeCustomer = activeCustomerFromNav;

	const { showError } = useNotificationActions();

	const {
		filteredData,
		data,
		isLoading: isDataLoading,
		isFetching: isDataFetching,
		isError,
		isUserConfigActionLoading,
		createUserConfig,
		updateUserConfig,
		deleteUserConfig,
		userConfigs,
		employeeId,
		customerId,
	} = useAddressBookFilteredData(activeCustomer?.id, selectedFilters);

	// Use a ref to ensure we only show the error notification once per transition to error state
	const hasShownErrorRef = useRef(false);

	useEffect(() => {
		if (isError || isCustomerError) {
			if (!hasShownErrorRef.current) {
				showError("Failed to load address book items. Please try again later.");
				hasShownErrorRef.current = true;
			}
		} else {
			hasShownErrorRef.current = false;
		}
	}, [isError, isCustomerError, showError]);

	const {
		updateAddressBookItem,
		isUpdating: isAddressBookItemUpdating,
		createAddressBookItem,
		isCreating: isAddressBookItemCreatingFromHook,
	} = useAddressBook();

	const isInitialLoading = isDataLoading || isCustomerLoading;

	const {
		addressBookColumns,
		isDeleting: isAddressBookItemDeleting,
		isCreating: isAddressBookItemColumnsCreating,
		DeleteConfirmationModal,
		UserDefaultConfirmation,
		handleSetUserDefault,
		handleRemoveUserDefault,
		favoriteItem,
		openDeleteConfirmation,
	} = useAddressBookColumns({
		isLoading: isInitialLoading,
		setSelectedAddressBookItem: handleSelectAddressBookItem,
		createUserConfig,
		updateUserConfig,
		deleteUserConfig,
		userConfigs,
		employeeId,
		customerId,
		onConfirmUserDefault: () => {
			if (pendingSubmitData) {
				finalizeSubmit(pendingSubmitData);
			}
		},
	});

	/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call -- Workaround for type inference issues with AddressBookAddressSubmitData */
	const finalizeSubmit = useCallback(
		async (data: any) => {
			let addressBookItemId = data.addressBookUpdate.addressBookItemId;

			if (data.isLegacy) {
				const createResponse = await createAddressBookItem({
					customerId: data.addressBookUpdate.customerId,
					companyName: data.addressBookUpdate.companyName ?? "",
					address1: data.addressBookUpdate.address1,
					address2: data.addressBookUpdate.address2 ?? undefined,
					postalCode: data.addressBookUpdate.postalCode ?? "",
					city: data.addressBookUpdate.city ?? "",
					stateProvince: data.addressBookUpdate.stateProvince ?? "",
					country: data.addressBookUpdate.country,
					legacyAddressId: data.addressBookUpdate.legacyAddressId ?? undefined,
					details: data.addressBookUpdate.details.map((detail: any) => ({
						contactName: detail.contactName,
						contactPhone: detail.contactPhone,
						contactExtension: detail.contactExtension ?? undefined,
						hoursOpen: detail.hoursOpen,
						hoursClose: detail.hoursClose,
						stopType: detail.stopType,
						mode: detail.mode,
						timeZone: detail.timeZone,
						specialInstructions: detail.specialInstructions ?? "",
						accessorials: detail.accessorials ?? undefined,
					})),
				});
				addressBookItemId = createResponse.addressBookItemId;
			} else {
				await updateAddressBookItem(data.addressBookUpdate);
			}

			// Handle Pickup Default
			if (data.isPickupDefault) {
				await handleSetUserDefault(addressBookItemId, "Pickup");
			} else {
				handleRemoveUserDefault(addressBookItemId, "Pickup");
			}

			// Handle Delivery Default
			if (data.isDeliveryDefault) {
				await handleSetUserDefault(addressBookItemId, "Delivery");
			} else {
				handleRemoveUserDefault(addressBookItemId, "Delivery");
			}

			setPendingSubmitData(null);
			handleSelectAddressBookItem(undefined);
		},
		[
			createAddressBookItem,
			updateAddressBookItem,
			handleSetUserDefault,
			handleRemoveUserDefault,
			handleSelectAddressBookItem,
		],
	);

	const handleFormSubmit = useCallback(
		(data: any) => {
			setPendingSubmitData(data);

			const addressBookItemId = data.addressBookUpdate.addressBookItemId;

			const needsPickupConfirmation =
				data.isPickupDefault &&
				userConfigs.some(
					(c) =>
						c.configType === ConfigurationType.PickDefaultAddress &&
						c.configValue !== addressBookItemId.toString() &&
						c.customerId === customerId,
				);

			const needsDeliveryConfirmation =
				data.isDeliveryDefault &&
				userConfigs.some(
					(c) =>
						c.configType === ConfigurationType.DropDefaultAddress &&
						c.configValue !== addressBookItemId.toString() &&
						c.customerId === customerId,
				);

			if (needsPickupConfirmation) {
				handleSetUserDefault(addressBookItemId, "Pickup");
			} else if (needsDeliveryConfirmation) {
				handleSetUserDefault(addressBookItemId, "Delivery");
			} else {
				// No confirmation needed, proceed with save
				finalizeSubmit(data);
			}
		},
		[userConfigs, handleSetUserDefault, finalizeSubmit, customerId],
	);
	/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call -- End workaround */

	// Internal and external users both use the server-backed preference path
	// (visibility, order, width) via independent endpoints/hooks. Both data and
	// sync hooks are called unconditionally; each self-disables its network
	// request for the other deployment, and the active set is selected by
	// deployment type. The external flow is left fully untouched.
	const { data: externalUserPreferences } = useGetUserPreferences(
		GridIdentifiers["Address Book"],
	);
	const { data: internalUserPreferences } = useGetInternalUserPreferences(
		GridIdentifiers["Address Book"],
	);
	const userPreferences = isInternal
		? internalUserPreferences
		: externalUserPreferences;

	const selectedColumns = useAddressBookGridColumns({
		columns: addressBookColumns,
		isInternal,
		columnPrefs: userPreferences?.columns,
	});

	const gridPrefsOptions = {
		defaultPrefs:
			getDefaultAddressBookGridPrefs(isInternal)[
				GridIdentifiers["Address Book"]
			]?.columns,
	};
	const externalGridPrefs = useGridPreferencesSync(
		GridIdentifiers["Address Book"],
		gridPrefsOptions,
	);
	const internalGridPrefs = useInternalGridPreferencesSync(
		GridIdentifiers["Address Book"],
		gridPrefsOptions,
	);
	const {
		gridKey: prefsGridKey,
		onColumnVisibilityChange: handleToggleColumn,
		onColumnReorder: handleColumnReorder,
		onColumnResize: handleColumnResize,
		resetColumns: handleResetColumns,
	} = isInternal ? internalGridPrefs : externalGridPrefs;

	const processedColumns = useMemo(
		() => selectedColumns.filter((col) => col.show),
		[selectedColumns],
	);

	// Both deployments render at their persisted/canonical widths; internal
	// width auto-distribution was removed so saved widths are honored.
	const gridColumns = useMemo(
		() => processedColumns.map((col) => ({ ...col })),
		[processedColumns],
	);

	const selectedItem = useMemo(() => {
		if (!selectedAddressBookItem) return undefined;
		return data.find((x) => x.addressBookItemId === selectedAddressBookItem);
	}, [data, selectedAddressBookItem]);

	// Mutations (save/update/delete/config changes) keep the full-screen
	// blocking spinner so the user can't interact mid-write.
	const isMutating =
		(isAddressBookItemUpdating ||
			isAddressBookItemCreatingFromHook ||
			isAddressBookItemColumnsCreating ||
			isUserConfigActionLoading ||
			isAddressBookItemDeleting) &&
		!isError &&
		!isCustomerError;

	// Initial/page data loading is surfaced via the TraxGrid skeleton instead
	// of a full-screen blocking loader.
	const isLoading =
		(isMutating || isInitialLoading || isDataFetching) &&
		!isError &&
		!isCustomerError;

	const shouldShowMissingItemState =
		Boolean(selectedAddressBookItem) &&
		!isLoading &&
		!isError &&
		!isCustomerError &&
		!selectedItem;

	useAutoSpinner(isMutating);

	const toggleAddAddressModal = () => {
		setShowAddAddressModal(!showAddAddressModal);
	};

	const handleSave = () => {
		if (!addressBookAddressRef.current) return;
		addressBookAddressRef.current.submitForm();
	};

	const handleCancel = () => {
		handleSelectAddressBookItem(undefined);
	};

	useEffect(() => {
		setSelectedFilters([]);
	}, [activeCustomer]);

	const renderContent = () => {
		if (selectedAddressBookItem) {
			if (selectedItem) {
				return (
					<AddressBookAddress
						ref={addressBookAddressRef}
						addressBookItem={selectedItem}
						onSubmit={handleFormSubmit}
					/>
				);
			}

			if (shouldShowMissingItemState) {
				return (
					<AddressBookAddress
						ref={addressBookAddressRef}
						addressBookItem={undefined}
						onSubmit={handleFormSubmit}
					/>
				);
			}

			return null;
		}

		return (
			<div className={"address-book-grid-section"}>
				<div className={"address-book-grid-section-fill-height"}>
					{isMobile ? (
						<MobileAddressBook
							addresses={filteredData}
							loading={isLoading || isLoadingPreference}
							onDelete={openDeleteConfirmation}
							onEdit={handleSelectAddressBookItem}
							onFavorite={favoriteItem}
							onSetUserDefault={(addressBookItemId, type) => {
								void handleSetUserDefault(addressBookItemId, type);
							}}
						/>
					) : (
						<div className={"address-book-grid-wrapper"}>
							<TraxGrid
								key={prefsGridKey}
								className={"address-book-grid"}
								columns={convertToGridColumnProps(gridColumns)}
								data={filteredData}
								dataItemKey={"addressBookItemId"}
								filter={filter}
								filterable={true}
								loading={isLoading || isLoadingPreference}
								loadingRows={10}
								onColumnReorder={handleColumnReorder}
								onColumnResize={handleColumnResize}
								onFilterChange={handleFilterChange}
								onPageChange={handlePageChange}
								onSortChange={handleSortChange}
								reorderable={true}
								skip={skip}
								sort={sort}
								style={{ height: "100%" }}
								take={take}
							/>
						</div>
					)}
					<div className={"add-address-button-container"}>
						<Button
							onClick={toggleAddAddressModal}
							svgIcon={plusCircleLine}
							variant={"primary"}
						>
							Add Address
						</Button>
					</div>
				</div>
			</div>
		);
	};

	return (
		<TraxPageLayout>
			<DeleteConfirmationModal />
			<UserDefaultConfirmation />
			<TraxPageHeader
				isLtlOnly={!isInternal}
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Address Book"}
			/>
			<div className={"address-book-content"}>
				<AddressBookHeader
					addressId={selectedAddressBookItem}
					columns={selectedColumns}
					isLegacy={selectedItem?.isLegacy ?? false}
					isLoading={isLoading}
					onCancel={handleCancel}
					onColumnToggle={handleToggleColumn}
					onResetColumns={handleResetColumns}
					onSave={handleSave}
					selectedAddressBookItem={selectedAddressBookItem}
					selectedCustomer={activeCustomer}
					selectedFilters={selectedFilters}
					setSelectedAddressBookItem={handleSelectAddressBookItem}
					setSelectedCustomer={(customer: CustomerDetails | null) => {
						handleSelectCustomer(customer);
					}}
					setSelectedFilters={setSelectedFilters}
					showEditActions={Boolean(selectedAddressBookItem && selectedItem)}
				/>
				{activeCustomer || isInitialLoading ? renderContent() : null}
			</div>
			{showAddAddressModal && activeCustomer ? (
				<AddAddressModal
					customerId={activeCustomer.id}
					onClose={toggleAddAddressModal}
				/>
			) : null}
		</TraxPageLayout>
	);
};
